// 📣 LIVE ROOM — the crowd inside the main feed (Nina 09-19;
// LIVE_ROOM_INLINE_GHOST_SPEC_2026_09_19.md, W1, treatment A ruled).
//
// A space created with the "Live room" chip carries
// `tool_flags.crowd_inline === true`. There the ghost lane does not exist as
// a lane: its lines render INSIDE the main chat feed as compact rows, and
// every ghost surface (right column, edge tab, phone sheet, lane bar) is off.
//
// PURE on purpose — no React, no DOM, no clock. GameStepPage only calls in;
// every rule that can go wrong (where it is allowed, ordering, the history
// horizon, the un-acked echo, the don't-split rule) is pinned in
// crowdInline.test.js.
//
// ⚠️ `crowd_inline` is OPT-IN TRUE (the `tool_flags.terminal` precedent). Every
// other tool flag is "missing = ON, explicit false = off". Do not flip this
// one to that doctrine: a missing key must mean the classic two-lane layout,
// or every legacy space would change shape overnight.

import { HORIZON_LINE_TYPE, HORIZON_ROW_ESTIMATE_PX } from './portedHorizon';
import { CHIP_IN_LINE_TYPE } from './chipIn'; // 💳 10-29 the lasting thank-you rides this merge too

export const CROWD_LINE_TYPE = 'crowd_line';

// T7 (Nina 09-20): under "Tags" on /custom-space, ONLY while the Live room chip
// is picked, tell the creator their tags pick the crowd.
// ⚠️ That sentence is only TRUE once the backend's LIVE_ROOM_CROWD_REACT_ENABLED
// is on (the room-fit voice draw rides that flag), and this page cannot see an
// env flag. So it ships OFF and gets flipped in the SAME sweep as the backend
// flag (the SHOW_CHIBI_PASSPORT_DOOR pattern). spaceGoalParity.test.js pins the
// value so the flip is a deliberate two-file edit, never a drive-by.
// 09-24: ON (Nina's go) — LIVE_ROOM_CROWD_REACT_ENABLED is live on 69/69 since 09-20.
export const SHOW_LIVE_TAGS_HINT = true;
export const LIVE_TAGS_HINT = 'In a Live room, your tags pick the crowd.';

// A resident's back-to-back messages inside this window are one thought; the
// crowd never lands between them (spec §4 spacing rule).
export const CROWD_NO_SPLIT_MS = 10000;

/**
 * 📣 09-25 (Nina, "Crowd as full lines"): when the backend runs the fast
 * stream here (status.crowd_react.stream, served only with
 * LIVE_ROOM_FAST_STREAM_ENABLED on), crowd lines render at the card text's
 * size and brightness instead of small + dim. Keyed on the server's word so
 * the look and the pace turn on in ONE moment. Callers AND this with
 * isCrowdInline (the one gate).
 */
export function crowdFullLines(status) {
  return status?.crowd_react?.stream === true;
}

// The feed virtualizer's first guess at a row's height, before it measures.
// A card is ~200px, a crowd line one short text row; a closer guess means
// fewer scroll corrections while a fast crowd streams in (every non-crowd
// row keeps the old 200, so no other feed changes).
// 🏡 W2c: a ported room's horizon line is one short printed row too.
export const CROWD_ROW_ESTIMATE_PX = 28;
export const CARD_ROW_ESTIMATE_PX = 200;
export const CHIP_IN_ROW_ESTIMATE_PX = 44;
export function estimateFeedRowPx(action) {
  if (action && action.type === HORIZON_LINE_TYPE) return HORIZON_ROW_ESTIMATE_PX;
  if (action && action.type === CHIP_IN_LINE_TYPE) return CHIP_IN_ROW_ESTIMATE_PX;
  return action && action.type === CROWD_LINE_TYPE ? CROWD_ROW_ESTIMATE_PX : CARD_ROW_ESTIMATE_PX;
}

const MESSAGE_TYPES = new Set(['public_message', 'human_interjection', 'private_message', 'secret_message']);

/**
 * Is this space's crowd inline for THIS viewer?
 * ⭐⭐ Competitive and imposter ghost chat is a SECRET channel (eliminated
 * players talk there, living players must not read it). Those modes, ported
 * 1:1 spaces (lane off by ruling) and bot mode (the explorer fleet types into
 * the ghost input by placeholder) can NEVER be inline, whatever the flag says.
 */
export function isCrowdInline({ status, isPortedSpace = false, isBotMode = false } = {}) {
  if (!status || status.tool_flags?.crowd_inline !== true) return false;
  if (status.competitive_mode || status.imposter_mode) return false;
  if (isPortedSpace || status.is_ported) return false;
  if (isBotMode) return false;
  return true;
}

/**
 * T9 (Nina 09-20): rows for the MAIN composer's @ dropdown, one per ghost
 * alias. `aliases` comes from status.crowd_react (present only when the
 * backend will actually answer, so an offered @alias always works).
 * ⭐ A crowd row can never become a `target_players` entry: the form computes
 * targets at submit from the `players` list alone, and an alias is not a
 * player. That is also why an alias that collides with a real player's name
 * is dropped here (the player row wins, and it WOULD target).
 * Never inside a slash command: a directive cannot target a ghost.
 */
export function crowdMentionRows({ aliases, query = '', players = [], draftIsCommand = false } = {}) {
  if (draftIsCommand || !Array.isArray(aliases) || aliases.length === 0) return [];
  const q = String(query || '').toLowerCase();
  const taken = new Set((players || []).map((p) => String(p?.name || '').toLowerCase()).filter(Boolean));
  const seen = new Set();
  const rows = [];
  for (const a of aliases) {
    if (typeof a !== 'string' || !a.trim()) continue;
    const name = a.trim();
    const key = name.toLowerCase();
    if (seen.has(key) || taken.has(key) || !key.startsWith(q)) continue;
    seen.add(key);
    rows.push({ name, isCrowd: true });
  }
  return rows;
}

const toTs = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (!v) return NaN;
  return Date.parse(v);
};

/**
 * Ghost-lane messages → stable pseudo-actions for the feed. Memoize the
 * result on the messages array: ActionRenderer-style rows are React.memo'd on
 * object identity, so these objects must not be rebuilt every render.
 * A line with no readable timestamp sorts last (it just arrived).
 */
export function buildCrowdItems(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const items = messages
    .filter((m) => m && (m.message || m.gif_url))
    .map((m, i) => {
      const ts = toTs(m.timestamp);
      return {
        id: `crowd_${m.id || `local_${i}`}`,
        type: CROWD_LINE_TYPE,
        created_at: Number.isFinite(ts) ? new Date(ts).toISOString() : null,
        _ts: Number.isFinite(ts) ? ts : Infinity,
        _seq: i,
        data: { crowd: m },
      };
    });
  items.sort((a, b) => (a._ts - b._ts) || (a._seq - b._seq));
  return items;
}

/**
 * Merge crowd items into ONE already-ordered feed list. Never reorders the
 * feed's own actions (rounds group analyses/episodes out of time order on
 * purpose) — it only chooses a slot for each crowd item:
 *   · slot = right after the LAST feed action (in list order) at or before it
 *   · horizon: a crowd line older than the oldest loaded feed action is
 *     dropped (the two lanes paginate independently; an orphan line with no
 *     conversation around it is noise). An EMPTY feed shows the whole crowd.
 *   · un-acked echo: never slot after the viewer's optimistic message — its
 *     created_at is a client clock. The echo stays last until the server acks.
 *   · don't split: never between two messages by the same seat <= 10s apart.
 * Returns the SAME array when there is nothing to add (memo-friendly).
 */
export function mergeCrowdIntoFeed(actions, crowdItems) {
  const feed = Array.isArray(actions) ? actions : [];
  if (!Array.isArray(crowdItems) || crowdItems.length === 0) return feed;
  if (feed.length === 0) return crowdItems;

  const n = feed.length;
  const ts = feed.map((a) => toTs(a && a.created_at));
  let horizon = Infinity;
  for (let i = 0; i < n; i++) if (Number.isFinite(ts[i]) && ts[i] < horizon) horizon = ts[i];
  if (!Number.isFinite(horizon)) return feed;

  // The trailing un-acked block (optimistic echoes always sit at the end).
  let firstTrailingEcho = n;
  while (firstTrailingEcho > 0 && feed[firstTrailingEcho - 1] && feed[firstTrailingEcho - 1].isOptimistic) firstTrailingEcho--;

  const sameThought = (a, b, i) => (
    a && b && MESSAGE_TYPES.has(a.type) && MESSAGE_TYPES.has(b.type)
    && a.game_instance_player_id && a.game_instance_player_id === b.game_instance_player_id
    && Number.isFinite(ts[i]) && Number.isFinite(ts[i + 1])
    && Math.abs(ts[i + 1] - ts[i]) <= CROWD_NO_SPLIT_MS
  );

  // slots[k] = crowd items rendered right AFTER feed[k]
  const slots = new Map();
  let placed = 0;
  for (const item of crowdItems) {
    if (item._ts < horizon) continue;
    let p = -1;
    for (let i = 0; i < n; i++) if (Number.isFinite(ts[i]) && ts[i] <= item._ts) p = i;
    if (p === -1) continue;
    while (p + 1 < n && sameThought(feed[p], feed[p + 1], p)) p++;
    if (p >= firstTrailingEcho) p = firstTrailingEcho - 1;
    if (p < 0) continue;
    if (!slots.has(p)) slots.set(p, []);
    slots.get(p).push(item);
    placed++;
  }
  if (placed === 0) return feed;

  const out = [];
  for (let i = 0; i < n; i++) {
    out.push(feed[i]);
    const here = slots.get(i);
    if (here) for (const it of here) out.push(it);
  }
  return out;
}

/**
 * One-entry memo around mergeCrowdIntoFeed, keyed on input IDENTITY. The feed
 * call site runs inside render; without this every render hands
 * VirtualizedActionList a fresh array and its React.memo never holds.
 */
export function createCrowdMergeCache() {
  let lastFeed = null;
  let lastCrowd = null;
  let lastOut = null;
  return (actions, crowdItems) => {
    if (actions === lastFeed && crowdItems === lastCrowd && lastOut) return lastOut;
    lastFeed = actions;
    lastCrowd = crowdItems;
    lastOut = mergeCrowdIntoFeed(actions, crowdItems);
    return lastOut;
  };
}
